import { useCallback, useId, useState, type ReactNode } from 'react'
import { useIntl } from 'react-intl'

import { cn, focusRing } from '@/shared/ui/cn'
import { ChevronDown } from '@/shared/ui/icons'

/**
 * One collapsible group in the filter sidebar, from the mockups' `find-search/FilterSection.jsx`:
 * a full-width row with the field's name, how many values are chosen and a chevron, then its
 * controls while open. It starts open when `isDefaultOpen` or when it already has a value.
 */
export function FilterSection(props: {
  readonly label: string
  /** How many values are chosen; shown beside the name, open or closed. */
  readonly count?: number
  readonly isDefaultOpen?: boolean
  readonly children: ReactNode
}) {
  const intl = useIntl()
  const count = props.count ?? 0
  const [isOpen, setOpen] = useState(props.isDefaultOpen === true || count > 0)
  const panelId = useId()
  const toggle = useCallback(() => setOpen((open) => !open), [])

  return (
    <section className="border-b border-border-strong">
      <h3>
        <button
          type="button"
          aria-expanded={isOpen}
          aria-controls={isOpen ? panelId : undefined}
          onClick={toggle}
          className={cn(
            'flex w-full cursor-pointer items-center justify-between gap-2 rounded py-4 text-left text-base font-semibold text-text-primary transition hover:text-primary-main',
            focusRing,
          )}
        >
          <span className="flex min-w-0 items-center gap-2 break-words">
            {props.label}{' '}
            {count > 0 && (
              <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-primary-main px-1.5 text-xs font-medium leading-5 text-primary-contrast">
                {count}{' '}
                <span className="sr-only">{intl.formatMessage({ id: 'search.filters.chosen' })}</span>
              </span>
            )}
          </span>
          <ChevronDown size={20} className={cn('shrink-0 text-text-primary transition', isOpen && 'rotate-180')} />
        </button>
      </h3>
      {isOpen && (
        <div id={panelId} className="flex flex-col gap-3 pb-5">
          {props.children}
        </div>
      )}
    </section>
  )
}
